/**
 * EditPatientDialog — Form dialog for editing patient demographics on a study.
 */
import { useEffect, useState, startTransition } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  Alert,
  Box,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { api } from '../../api/rpc';
import type { Study } from '../../api/types';

interface EditPatientDialogProps {
  open: boolean;
  study: Study | null;
  onClose: () => void;
  onComplete?: () => void;
}

const SEX_OPTIONS = [
  { value: '', label: '(unchanged)' },
  { value: 'M', label: 'Male' },
  { value: 'F', label: 'Female' },
  { value: 'O', label: 'Other' },
];

export const EditPatientDialog = ({
  open,
  study,
  onClose,
  onComplete,
}: EditPatientDialogProps) => {
  const [patientName, setPatientName] = useState('');
  const [dob, setDob] = useState('');
  const [sex, setSex] = useState('');
  const [ssn, setSsn] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Populate fields from study when dialog opens
  useEffect(() => {
    if (open && study) {
      startTransition(() => {
        setPatientName(study.name ?? '');
        setDob(study.dob ?? '');
        setSex(study.sex ?? '');
        setSsn('');
        setAddress('');
        setCity('');
        setState('');
        setZip('');
        setPhone('');
        setEmail('');
        setError(null);
      });
    }
  }, [open, study]);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!study) return;
      await api.editPatient({
        stuKey: study.objKey,
        patientName: patientName || undefined,
        dob: dob || undefined,
        sex: sex || undefined,
        ssn: ssn || undefined,
        address: address || undefined,
        city: city || undefined,
        state: state || undefined,
        zip: zip || undefined,
        phone: phone || undefined,
        email: email || undefined,
      });
    },
    onSuccess: () => {
      setError(null);
      onComplete?.();
      onClose();
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  if (!study) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Patient — {study.name}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 1 }}>
          <TextField
            fullWidth
            label="Patient Name"
            value={patientName}
            onChange={(e) => setPatientName(e.target.value)}
            size="small"
            sx={{ gridColumn: '1 / -1' }}
          />
          <TextField
            fullWidth
            label="Date of Birth"
            value={dob}
            onChange={(e) => setDob(e.target.value)}
            size="small"
            placeholder="yyyy/MM/dd"
          />
          <TextField
            select
            fullWidth
            label="Sex"
            value={sex}
            onChange={(e) => setSex(e.target.value)}
            size="small"
          >
            {SEX_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            label="SSN"
            value={ssn}
            onChange={(e) => setSsn(e.target.value)}
            size="small"
          />
          <TextField
            fullWidth
            label="Phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            size="small"
          />
          <TextField
            fullWidth
            label="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            size="small"
            sx={{ gridColumn: '1 / -1' }}
          />
          <TextField
            fullWidth
            label="Address"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            size="small"
            sx={{ gridColumn: '1 / -1' }}
          />
          <TextField
            fullWidth
            label="City"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            size="small"
          />
          <TextField
            fullWidth
            label="State"
            value={state}
            onChange={(e) => setState(e.target.value)}
            size="small"
          />
          <TextField
            fullWidth
            label="ZIP"
            value={zip}
            onChange={(e) => setZip(e.target.value)}
            size="small"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
